/**
 * UI/UX's elements on the board (Figma's), drawn as the prototype draws them but not working: a
 * button, an input (its words a hint, `style.secret` hiding them), a checkbox (`style.checked`
 * ticked), a list (a row per line of its words) and an instance of a component (its parts, from
 * `components.ts`, scaled into it; a double click on one writes the instance's own words for it,
 * `[data-instance-part]`). Each is data the board's engine draws through (`shared/elements.ts`
 * `ElementDef`); their CSS is `uiux.css`.
 */
import { For, Show, createMemo, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import type { BoardPlace } from "@protocol/BoardPlace";
import { useApp } from "../../app/context";
import { fillIf, frameLook, lookOf, safeColour, type ElementBodyProps, type ElementDef } from "../../shared/elements";
import { ACCENT, ELEMENT_WORDS_PX, INPUT_LOOK } from "./constants";
import { instanceParts, overrideKey, type InstancePart } from "./components";

/** Where a place puts a part inside its instance (CSS pixels of the instance). */
function placed(place: BoardPlace): JSX.CSSProperties {
  return { left: `${place.x}px`, top: `${place.y}px`, width: `${place.w}px`, height: `${place.h}px`, ...(place.rotate ? { transform: `rotate(${place.rotate}deg)` } : {}) };
}

/** An input: its words as a hint (hidden ones as dots). */
function InputBody(props: ElementBodyProps): JSX.Element {
  return <span class="board-hint">{props.style.secret === "yes" ? "••••••" : props.text}</span>;
}

/** A checkbox: its tick and its words. */
function CheckboxBody(props: ElementBodyProps): JSX.Element {
  return (
    <>
      <span class="board-tick" classList={{ on: props.style.checked === "yes" }} />
      <span>{props.text}</span>
    </>
  );
}

/** A list: a row per line of its words. */
function ListBody(props: ElementBodyProps): JSX.Element {
  return <For each={props.text.split("\n").filter((l) => l.trim())}>{(line) => <div class="board-row">{line}</div>}</For>;
}

/** An instance's parts, placed inside it; `editingPart`: the one whose own words are being written. */
function Parts(props: { parts: InstancePart[]; editingPart: string | null; write: (part: string, text: string) => void; done: () => void; body: ElementBodyProps }): JSX.Element {
  const { kinds } = useApp();
  return (
    <For each={props.parts}>
      {(part) => {
        const look = () => (part.node ? kinds.board(part.node.kind) : null);
        const element = () => look()?.element ?? null;
        const css = (): JSX.CSSProperties => ({ ...placed(part.place), ...((look() ? lookOf(look()!.def, part.style) : frameLook(part.style)) as JSX.CSSProperties) });
        return (
          <div
            class={`board-part board-${element() ?? "box"}`}
            classList={{ [`shape-${part.style.shape ?? "rectangle"}`]: element() === "shape" }}
            data-instance-part={part.id}
            data-writable={look() && !look()!.def.wordless ? "yes" : undefined}
            style={css()}
          >
            <Show when={part.node}>
              {(node) => (
                <Show
                  when={props.editingPart === part.id}
                  fallback={
                    <Show when={part.parts.length} fallback={<Dynamic component={props.body.Inner} look={look()!} text={part.text} style={part.style} attachments={node().attachments} />}>
                      <Parts parts={part.parts} editingPart={null} write={props.write} done={props.done} body={props.body} />
                    </Show>
                  }
                >
                  <Dynamic component={props.body.Words} text={part.text} editing={true} done={props.done} write={(text: string) => props.write(part.id, text)} />
                </Show>
              )}
            </Show>
          </div>
        );
      }}
    </For>
  );
}

/** An instance: its component's parts scaled into it, on its component's frame look. */
function InstanceBody(props: ElementBodyProps): JSX.Element {
  const { board, graph, actions } = useApp();
  const made = createMemo(() => {
    const item = props.item;
    return item ? instanceParts(graph.state.graph, board.content(), item.node, item.place) : null;
  });
  /** Writes the instance's own words for a part (the component's words again: the override goes). */
  const write = (part: string, text: string) => {
    const item = props.item;
    const own = made()?.parts.find((p) => p.id === part);
    if (!item || !own) return;
    const original = own.node?.name ?? "";
    const next = text.replace(/\s+$/, "");
    if (next === (props.style[overrideKey(part)] ?? original)) return;
    void actions.updateNode(item.id, { style: { [overrideKey(part)]: next === original ? "" : next } });
  };
  return (
    <Show when={made()} fallback={<div class="board-picture-missing">{props.text}: its component is gone</div>}>
      {(m) => (
        <div class="board-instance-frame" style={frameLook(m().frame) as JSX.CSSProperties}>
          <Parts parts={m().parts} editingPart={props.item?.editingPart ?? null} write={write} done={() => props.item?.done()} body={props} />
        </div>
      )}
    </Show>
  );
}

/** A button's tool picture. */
const ButtonIcon = () => (
  <>
    <rect x="1.5" y="4.5" width="13" height="7" rx="3" />
    <path d="M5.5 8h5" />
  </>
);

/** An input's tool picture. */
const InputIcon = () => (
  <>
    <rect x="1.5" y="4.5" width="13" height="7" rx="1" />
    <path d="M4.5 6.5v3" />
  </>
);

/** A checkbox's tool picture. */
const CheckboxIcon = () => (
  <>
    <rect x="2" y="3" width="10" height="10" rx="1.5" />
    <path d="M4.5 8l2 2 3.5-4" />
  </>
);

/** A list's tool picture. */
const ListIcon = () => <path d="M5.5 4h8M5.5 8h8M5.5 12h8M2.5 4h.5M2.5 8h.5M2.5 12h.5" />;

/** UI/UX's elements. */
export const UI_ELEMENTS: readonly ElementDef[] = [
  {
    id: "button",
    usual: { size: { w: 160, h: 44 }, words: "Button", style: {} },
    Icon: ButtonIcon,
    wordsPx: ELEMENT_WORDS_PX,
    look(style, css, align) {
      const stroke = safeColour(style.stroke);
      css.background = safeColour(style.fill) ?? ACCENT;
      css.color ??= "#ffffff";
      css["border-radius"] ??= "8px";
      css.border = stroke ? `1.5px solid ${stroke}` : "none";
      css["justify-content"] = align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center";
    },
  },
  {
    id: "input",
    usual: { size: { w: 280, h: 44 }, words: "Type here", style: {} },
    wordsPx: ELEMENT_WORDS_PX,
    align: "left",
    look(style, css) {
      css.background = safeColour(style.fill) ?? INPUT_LOOK.fill;
      css.border = `1px solid ${safeColour(style.stroke) ?? INPUT_LOOK.stroke}`;
      css["border-radius"] ??= "6px";
      css.color ??= INPUT_LOOK.color;
    },
    Body: InputBody,
    Icon: InputIcon,
  },
  { id: "checkbox", usual: { size: { w: 200, h: 28 }, words: "Option", style: {} }, wordsPx: ELEMENT_WORDS_PX, align: "left", look: fillIf, Body: CheckboxBody, Icon: CheckboxIcon },
  { id: "list", usual: { size: { w: 240, h: 120 }, words: "First\nSecond\nThird", style: {} }, wordsPx: ELEMENT_WORDS_PX, align: "left", look: fillIf, Body: ListBody, Icon: ListIcon },
  {
    id: "instance",
    usual: { size: { w: 220, h: 120 }, words: "", style: {} },
    wordless: true,
    Body: InstanceBody,
    partAt: (target) => target.closest<HTMLElement>("[data-instance-part][data-writable]")?.dataset.instancePart ?? null,
  },
];
